import { useForm } from '@inertiajs/react';
import { useState, type FormEvent } from 'react';

import { AutoTextarea } from '@/components/admin/forms/AutoTextarea';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';
import { inputClass } from '@/lib/formStyles';

interface DocumentFormProps {
  categories: string[];
  onClose: () => void;
}

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';

export function DocumentForm({ categories, onClose }: DocumentFormProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const form = useForm({
    title: '',
    description: '',
    category: '',
    year: new Date().getFullYear(),
    date: '',
    file: null as File | null,
  });

  // Transform flat fields into the nested structure Rails expects
  form.transform((data) => ({
    document: {
      title: data.title,
      description: data.description,
      category: data.category,
      year: data.year,
      date: data.date,
    },
    file: data.file,
  }));

  function submit(e: FormEvent) {
    e.preventDefault();
    trackUmamiEvent('admin_content_create', { content_type: 'document' });
    form.post('/documents', {
      forceFormData: true,
      onSuccess: () => onClose(),
    });
  }

  const categoryOptions = categories.map((c) => ({ label: c, value: c }));

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className={labelClass} htmlFor="doc-title">
          Título <span className="text-error">*</span>
        </label>
        <input
          id="doc-title"
          type="text"
          value={form.data.title}
          onChange={(e) => form.setData('title', e.target.value)}
          required
          className={inputClass}
          placeholder="Ex: Ata da Reunião de Março"
        />
        {form.errors.title && <p className="text-error mt-1 text-xs">{form.errors.title}</p>}
      </div>

      <div>
        <label className={labelClass} htmlFor="doc-description">
          Descrição <span className="text-error">*</span>
        </label>
        <AutoTextarea
          id="doc-description"
          value={form.data.description}
          onChange={(e) => form.setData('description', e.target.value)}
          required
          rows={3}
          placeholder="Breve descrição do documento..."
        />
        {form.errors.description && (
          <p className="text-error mt-1 text-xs">{form.errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <span className={labelClass}>
            Categoria <span className="text-error">*</span>
          </span>
          <Select
            options={[{ label: 'Selecionar...', value: '' }, ...categoryOptions]}
            value={form.data.category}
            onChange={(v) => form.setData('category', v)}
            placeholder="Selecionar..."
            className="w-full"
          />
          {form.errors.category && (
            <p className="text-error mt-1 text-xs">{form.errors.category}</p>
          )}
        </div>

        <div>
          <label className={labelClass} htmlFor="doc-year">
            Ano <span className="text-error">*</span>
          </label>
          <input
            id="doc-year"
            type="number"
            value={form.data.year}
            onChange={(e) => form.setData('year', parseInt(e.target.value, 10))}
            required
            min={1900}
            max={2100}
            className={inputClass}
          />
          {form.errors.year && <p className="text-error mt-1 text-xs">{form.errors.year}</p>}
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="doc-date">
          Data <span className="text-error">*</span>
        </label>
        <input
          id="doc-date"
          type="date"
          value={form.data.date}
          onChange={(e) => form.setData('date', e.target.value)}
          required
          className={inputClass}
        />
        {form.errors.date && <p className="text-error mt-1 text-xs">{form.errors.date}</p>}
      </div>

      <div>
        <span className={labelClass}>
          Ficheiro PDF <span className="text-error">*</span>
        </span>
        <div className="flex items-center gap-3">
          <label className="border-line-strong text-on-surface hover:border-primary hover:text-primary bg-surface inline-flex cursor-pointer items-center gap-2 rounded-md border px-3.5 py-2 text-sm font-semibold transition-colors">
            <span className="material-symbols-outlined text-[17px]">cloud_upload</span>
            {fileName ? 'Alterar ficheiro' : 'Escolher ficheiro PDF'}
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                form.setData('file', f);
                setFileName(f?.name ?? null);
              }}
              className="hidden"
            />
          </label>
          {fileName && (
            <span className="text-on-surface-light inline-flex min-w-0 items-center gap-1.5 text-sm">
              <span className="material-symbols-outlined text-on-surface-light/60 text-[16px]">
                picture_as_pdf
              </span>
              <span className="truncate">{fileName}</span>
            </span>
          )}
        </div>
        {form.errors.file && <p className="text-error mt-1 text-xs">{form.errors.file}</p>}
      </div>

      <div className="border-line flex justify-end gap-3 border-t pt-4">
        <Button
          type="button"
          variant="white"
          size="sm"
          onClick={onClose}
          disabled={form.processing}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={form.processing}
          loading={form.processing}
        >
          {form.processing ? 'A guardar...' : 'Guardar'}
        </Button>
      </div>
    </form>
  );
}
